import { useState, useCallback, useEffect, useRef } from 'react';
import { Container, Typography, Grid, Button, Card, CardContent } from '@mui/material';
import { Favorite, PlayArrow, ErrorOutline } from '@mui/icons-material';
import TerrainMap from '../components/TerrainMap';
import { API_URL } from '../config/api';
import { publicFetch } from '../utils/authenticatedFetch';
import { formatFullVolume } from '../utils/reviewHelpers';

const FIRST_STUPA_YEAR = 2020;

const HomePage = () => {
  const [niceCount, setNiceCount] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  
  // Real data states
  const [realMetrics, setRealMetrics] = useState({
    totalWaterManaged: 0,      // sum_flowrate_spraying + sum_flowrate_excess (m³)
    totalWaterReplenished: 0,  // sum of flow_increment (m³) 
    activeStupas: 0,           // count from icestupas API
    operationalHours: 0,       // sum of count_hours_spraying
    averagePerStupa: 0         // totalWaterReplenished / activeStupas
  });
  const [loading, setLoading] = useState(true);
  const [metricsError, setMetricsError] = useState(false);
  
  const [currentStoryIndex, setCurrentStoryIndex] = useState(1);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [globalNiceCount, setGlobalNiceCount] = useState(100); // Global counter across all users
  const [floatingHearts, setFloatingHearts] = useState<Array<{id: number, type: 'user' | 'global', x: number, y: number}>>([]);
  const heartIdCounter = useRef(0);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoSectionRef = useRef<HTMLDivElement>(null);
  const yearsSinceFirstStupa = Math.max(
    0,
    new Date().getFullYear() - FIRST_STUPA_YEAR
  );
  const supportSectionRef = useRef<HTMLDivElement>(null);

  // Real articles and news stories - clickable links
  const stories = [
    {
      image: '/images/100_accelerator_article.jpeg',
      title: '100 Accelerator Partnership',
      description: 'Strategic partnership announcement with 100+ Accelerator for sustainable innovation',
      location: 'LinkedIn • Partnership News',
      link: 'https://www.linkedin.com/posts/nilus-project_ashleywilliams-100accelerator-impact-activity-7308699876437950464-1spe/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAB6paj0BzkR53ZTBQy-vFXYwK8lKhY2L97Y',
      type: 'linkedin'
    },
    {
      image: '/images/big_glaciers.jpeg',
      title: 'Austrian Business Hub Prize',
      description: 'Chile is beating its drought using gravity-fed glacier technology',
      location: 'LinkedIn • Award Recognition',
      link: 'https://www.linkedin.com/posts/advantage-austria-chile_chile-is-beating-its-drought-using-gravity-ugcPost-7321172079909785602-9JVI/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAB6paj0BzkR53ZTBQy-vFXYwK8lKhY2L97Y',
      type: 'linkedin'
    },
    {
      image: '/images/groundprobe_blog.png',
      title: 'Fighting Glacier Retreat',
      description: 'GroundProbe report on combating glacier retreat and water scarcity in Chile',
      location: 'GroundProbe • Technical Article',
      link: 'https://www.groundprobe.com/fighting-glacier-retreat-and-water-scarcity-in-chile/',
      type: 'article'
    },
    {
      image: '/images/anton-seba.png',
      title: 'COPEC Collaboration',
      description: 'Visit to NILUS Hydric Park showcasing sustainable water preservation technology',
      location: 'LinkedIn • Partnership Visit',
      link: 'https://www.linkedin.com/posts/arturo-natho_visita-parque-hídrico-nilus-activity-7251628816928763904-De91/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAAM74-oB24HHKCK1Gx8FiT-c3hVAVdxgGoI',
      type: 'linkedin'
    },
    {
      image: '/images/airbus.png',
      title: 'Airbus EcoCopters Partnership',
      description: 'Creating glaciers with H125 helicopter technology in Chile\'s mountains',
      location: 'Airbus • Innovation Story',
      link: 'https://www.airbus.com/en/newsroom/stories/2024-03-creating-glaciers-the-h125-helps-keep-the-chill-in-chiles-mountains',
      type: 'article'
    },
    {
      image: '/images/lidar.jpeg',
      title: 'RIEGL LiDAR Technology',
      description: 'Advanced LiDAR scanning technology for precise glacier monitoring',
      location: 'LinkedIn • Technology Feature',
      link: 'https://www.linkedin.com/posts/nilus-project_southamerica-lidarvz-southamerica-activity-7325142382960664576-Nge0/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAB6paj0BzkR53ZTBQy-vFXYwK8lKhY2L97Y',
      type: 'linkedin'
    },
    {
      image: '/images/real_stupas/spouting-stupa-Nilus.jpg',
      title: 'NexNews Coverage',
      description: 'Chilean media coverage of NILUS glacier innovation project',
      location: 'NexNews • Media Coverage',
      link: 'https://portal.nexnews.cl/showN?valor=23p0do',
      type: 'article'
    },
    {
      image: '/images/harvard-innovation-lab.jpg',
      title: 'Harvard Climate Circle Selection',
      description: 'NILUS selected for Harvard Climate Entrepreneur\'s Circle 2022 cohort',
      location: 'Harvard Innovation Labs • Recognition',
      link: 'https://innovationlabs.harvard.edu/articles/climate-circle-2022',
      type: 'article'
    },
  ];

  // Create extended stories array for infinite scroll
  const extendedStories = [
    stories[stories.length - 1], // Last item at the beginning
    ...stories,
    stories[0] // First item at the end
  ];

  // Fetch real data from backend
  useEffect(() => {
    const fetchRealData = async () => {
      try {
        setLoading(true);
        setMetricsError(false);
        
        // Get ALL-TIME data (no date restrictions for total impact)
        const [stupaSummaryResponse, replenishmentResponse, likesResponse] = await Promise.all([
          publicFetch(`${API_URL}/public/stupas/summary`),
          publicFetch(`${API_URL}/public/impact/replenishment-summary`), // New endpoint for proper water calculations
          // Likes are optional and must not prevent impact metrics from loading.
          publicFetch(`${API_URL}/likes`).catch((error) => {
            console.warn('Unable to load global support count:', error);
            return null;
          })
        ]);

        if (stupaSummaryResponse.ok && replenishmentResponse.ok) {
          const stupaSummary = await stupaSummaryResponse.json();
          const replenishmentData = await replenishmentResponse.json();
          
          // Get like count data
          if (likesResponse?.ok) {
            const likesData = await likesResponse.json();
            setGlobalNiceCount(likesData.globalLikeCount);
          }
          
          // Real metric 1: Number of active stupas (current)
          const activeStupas = stupaSummary.stupas.length;
          
          // Real metric 2: Total water managed from replenishment table (ALL TIME)
          const totalWaterManaged = replenishmentData.total_managed || 0;
          
          // Real metric 3: Total water replenished = total_infiltrated + ice_stored (ALL TIME)
          const totalWaterReplenished = replenishmentData.total_replenished || 0;
          
          // Real metric 4: Total operational hours = sum of count_hours_spraying (ALL TIME)
          const operationalHours = replenishmentData.operational_hours || 0;
          
          // Real metric 5: Average water saved per stupa (calculated from real data)
          const averagePerStupa = activeStupas > 0 ? totalWaterReplenished / activeStupas : 0;
          
          setRealMetrics({
            totalWaterManaged: Math.round(totalWaterManaged),
            totalWaterReplenished,
            activeStupas,
            operationalHours: Math.round(operationalHours),
            averagePerStupa: Math.round(averagePerStupa)
          });
        } else {
          setMetricsError(true);
          // Fallback to placeholders if API fails - indicates manual configuration needed
          setRealMetrics({
            totalWaterManaged: 0, // Will show as "TBD" in UI
            totalWaterReplenished: 0, // Will show as "TBD" in UI
            activeStupas: 0, // Will show as "TBD" in UI
            operationalHours: 0, // Will show as "TBD" in UI
            averagePerStupa: 0 // Will show as "TBD" in UI
          });
        }
      } catch (error) {
        console.error('Error fetching real data:', error);
        setMetricsError(true);
        // Fallback to placeholders - indicates manual configuration needed
        setRealMetrics({
          totalWaterManaged: 0, // Will show as "TBD" in UI
          totalWaterReplenished: 0, // Will show as "TBD" in UI
          activeStupas: 0, // Will show as "TBD" in UI
          operationalHours: 0, // Will show as "TBD" in UI
          averagePerStupa: 0 // Will show as "TBD" in UI
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRealData();
  }, []);

  const handleScroll = useCallback(() => {
    const scrollPosition = window.scrollY;
    setScrollY(scrollPosition);
  }, []);

  const scrollToVideo = () => {
    videoSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToSupport = () => {
    supportSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const nextStory = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentStoryIndex(prev => prev + 1);
  };

  const prevStory = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentStoryIndex(prev => prev - 1);
  };

  // Handle infinite scroll transitions
  useEffect(() => {
    if (isTransitioning) {
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        
        // Handle wrapping
        if (currentStoryIndex >= stories.length + 1) {
          setCurrentStoryIndex(1); // Jump to first real item
        } else if (currentStoryIndex <= 0) {
          setCurrentStoryIndex(stories.length); // Jump to last real item
        }
      }, 500); // Match transition duration
      
      return () => clearTimeout(timer);
    }
  }, [currentStoryIndex, isTransitioning, stories.length]);

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextStory();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Create floating heart effect
  const createFloatingHeart = useCallback((type: 'user' | 'global') => {
    const newHeart = {
      id: heartIdCounter.current++,
      type,
      x: Math.random() * 200 - 100, // Random x offset from center
      y: 0
    };
    
    setFloatingHearts(prev => [...prev, newHeart]);
    
    // Remove heart after animation completes
    setTimeout(() => {
      setFloatingHearts(prev => prev.filter(heart => heart.id !== newHeart.id));
    }, 3000);
  }, []);

  // Live metrics ticker - REMOVED since we're using real data
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     // Real data is now fetched from API, no need for artificial increments
  //   }, 3000);
  //   return () => clearInterval(interval);
  // }, []);

  // Global NIIIIICE counter ticker
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.3) { // 30% chance every 4 seconds
        const increment = Math.floor(Math.random() * 3) + 1;
        setGlobalNiceCount(prev => prev + increment);
        
        // Create one heart for each increment (one heart per like)
        for (let i = 0; i < increment; i++) {
          setTimeout(() => createFloatingHeart('global'), i * 300);
        }
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [createFloatingHeart]);

  // Create single heart for user clicks (one heart per like)
  const createUserHeart = () => {
    createFloatingHeart('user');
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div style={{ 
      position: 'relative', 
      minHeight: '100vh'
    }}>
      {/* Video Background - Absolute positioned behind everything */}
      <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        zIndex: -1,
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', // Fallback background
        backgroundImage: 'url(/images/glacier_alpha.jpg)', // Fallback image
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        
        {/* Video Loading Indicator */}
        {!videoLoaded && !videoError && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '14px',
            zIndex: 1
          }}>
            Loading video...
          </div>
        )}
        
        {/* Video Error Message */}
        {videoError && (
          <div style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(244,67,54,0.1)',
            color: 'rgba(244,67,54,0.8)',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            border: '1px solid rgba(244,67,54,0.2)',
            zIndex: 1
          }}>
            Video unavailable - using fallback background
          </div>
        )}
        {!videoError && (
          <video
            src="/Nilus-Blue-Giants-2023.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            onLoadStart={() => {
              console.log('Video loading started');
              setVideoLoaded(false);
            }}
            onCanPlay={() => {
              console.log('Video can play');
              setVideoLoaded(true);
            }}
            onLoadedData={() => {
              console.log('Video loaded successfully');
              setVideoLoaded(true);
            }}
            onError={(e) => {
              console.error('Video failed to load:', e);
              console.error('Video src:', (e.target as HTMLVideoElement).src);
              console.error('Video currentSrc:', (e.target as HTMLVideoElement).currentSrc);
              setVideoError(true);
              setVideoLoaded(false);
            }}
            onStalled={() => {
              console.warn('Video stalled - network issue');
            }}
            onSuspend={() => {
              console.warn('Video suspended - loading paused');
            }}
            style={{ 
              position: 'absolute', 
              top: '50%',
              left: '50%',
              width: '100vw', 
              height: '100vh', 
              objectFit: 'cover',
              filter: 'blur(1px)',
              transform: 'translate(-50%, -50%) scale(1.1)',
              zIndex: -1,
              opacity: videoLoaded ? 1 : 0,
              transition: 'opacity 1s ease-in-out'
          }}
          >
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {/* Main Content */}
      <div style={{
          position: 'relative',
          zIndex: 1,
        background: 'transparent',
        minHeight: '100vh',
        isolation: 'isolate'
      }}>
        <Container maxWidth="lg" style={{ paddingTop: '100px' }}>
          
          {/* Hero Section - Enhanced Splash Screen */}
          <div ref={videoSectionRef} style={{ 
            position: 'relative',
            zIndex: 2,
            textAlign: 'center', 
            marginBottom: '100px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(240,248,255,0.95) 100%)',
            borderRadius: '40px',
            boxShadow: '0 30px 80px rgba(0,0,0,0.15), 0 0 0 1px rgba(33,150,243,0.1)',
            border: '2px solid rgba(33,150,243,0.2)',
            overflow: 'hidden',
            animation: 'heroFloat 6s ease-in-out infinite',
            isolation: 'isolate'
          }}>
            {/* Animated Background Elements */}
            <div style={{
              position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
              background: 'linear-gradient(45deg, rgba(33,150,243,0.03) 0%, rgba(25,118,210,0.05) 50%, rgba(13,71,161,0.03) 100%)',
              zIndex: 1
            }}>
              {/* Floating Ice Crystals */}
              {[...Array(6)].map((_, i) => (
                <div
                  key={`hero-crystal-${i}`}
                  style={{
                    position: 'absolute',
                    width: '12px',
                    height: '12px',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(173,216,230,0.6) 100%)',
                    clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)',
                    right: `${10 + (i * 12)}%`,
                    top: `${15 + (i * 10) % 70}%`,
                    animationName: 'crystalFloat',
                    animationDuration: `${4 + Math.random() * 2}s`,
                    animationTimingFunction: 'ease-in-out',
                    animationIterationCount: 'infinite',
                    animationDelay: `${i * 0.7}s`,
                    filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.6))'
                  }}
                />
              ))}
            </div>

            {/* Main Content */}
            <div style={{ position: 'relative', zIndex: 2, padding: '60px 40px' }}>
              {/* Animated Title with Gradient */}
              <div style={{ marginBottom: '40px' }}>
              <Typography 
                variant="h1" 
                  style={{ 
                    fontSize: '4.5rem',
                    fontWeight: 200,
                    marginBottom: '20px',
                    background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 50%, #42a5f5 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.1,
                    animation: 'titlePulse 4s ease-in-out infinite',
                    textShadow: '0 0 30px rgba(33,150,243,0.3)'
                  }}
                >
                  Every Drop Matters
              </Typography>
              </div>

              {/* Real Stupa Images with Floating Animation */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '30px', 
                marginBottom: '40px',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundImage: 'url(/images/real_stupas/guy-looking-up-at-ice-stupa.jpeg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '4px solid rgba(33,150,243,0.3)',
                  boxShadow: '0 15px 40px rgba(33,150,243,0.2)',
                  animation: 'imageFloat1 5s ease-in-out infinite',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(33,150,243,0.2) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                    borderRadius: '50%'
                  }} />
                </div>
                
                <div style={{
                  width: '140px',
                  height: '140px',
                  borderRadius: '50%',
                  backgroundImage: 'url(/images/real_stupas/center_image.png)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '5px solid rgba(25,118,210,0.4)',
                  boxShadow: '0 20px 50px rgba(25,118,210,0.3)',
                  animation: 'imageFloat2 4s ease-in-out infinite',
                  position: 'relative',
                  overflow: 'hidden',
                  transform: 'scale(1.1)'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(25,118,210,0.2) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                    borderRadius: '50%'
                  }} />
                </div>
                
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '50%',
                  backgroundImage: 'url(/images/watercolor/2025-05-30_NILUS_UX_01.jpg)',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  border: '4px solid rgba(42,165,245,0.3)',
                  boxShadow: '0 15px 40px rgba(42,165,245,0.2)',
                  animation: 'imageFloat3 6s ease-in-out infinite',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(135deg, rgba(42,165,245,0.2) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                    borderRadius: '50%'
                  }} />
                </div>
              </div>
              
              {/* Enhanced Description with Typewriter Effect */}
              <Typography 
                variant="h4" 
                style={{ 
                  fontSize: '1.9rem',
                  fontWeight: 400,
                  marginBottom: '50px',
                  color: '#424242',
                  lineHeight: 1.6,
                  maxWidth: '900px',
                  margin: '0 auto 50px auto',
                  textShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                In Chile's mountains, ancient glaciers are disappearing. But innovative technology is capturing their meltwater before it's lost forever—creating artificial ice reserves called{' '}
                <span style={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 600,
                  animation: 'textGlow 2s ease-in-out infinite'
                }}>
                  stupas
                </span>{' '}
                that secure water for entire communities.
              </Typography>

              {/* Enhanced Action Buttons */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '25px', 
                flexWrap: 'wrap',
                marginTop: '50px'
              }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PlayArrow />}
                  onClick={() => window.open('https://youtu.be/Ty9z1Ozz5Hg', '_blank')}
                  style={{ 
                    padding: '20px 45px',
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    borderRadius: '50px',
                    textTransform: 'none',
                    boxShadow: '0 12px 35px rgba(25,118,210,0.4)',
                    border: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(-3px) scale(1.05)';
                    (e.target as HTMLButtonElement).style.boxShadow = '0 20px 50px rgba(25,118,210,0.5)';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(0px) scale(1)';
                    (e.target as HTMLButtonElement).style.boxShadow = '0 12px 35px rgba(25,118,210,0.4)';
                  }}
                >
                  <span style={{ position: 'relative', zIndex: 2 }}>Watch Our Story</span>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    animation: 'shimmer 3s ease-in-out infinite'
                  }} />
                </Button>
                
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Favorite />}
                  onClick={scrollToSupport}
                  style={{ 
                    padding: '20px 45px',
                    fontSize: '1.3rem',
                    fontWeight: 600,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    borderWidth: '3px',
                    borderRadius: '50px',
                    textTransform: 'none',
                    background: 'rgba(255,255,255,0.8)',
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                  onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(-3px) scale(1.05)';
                    (e.target as HTMLButtonElement).style.background = 'rgba(25,118,210,0.1)';
                    (e.target as HTMLButtonElement).style.borderColor = '#42a5f5';
                  }}
                  onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(0px) scale(1)';
                    (e.target as HTMLButtonElement).style.background = 'rgba(255,255,255,0.8)';
                    (e.target as HTMLButtonElement).style.borderColor = '#1976d2';
                  }}
                >
                  Support Our Mission
                </Button>
              </div>

              {/* Live Impact Stats Ticker */}
              <div style={{
                marginTop: '50px',
                padding: '25px 35px',
                background: 'linear-gradient(135deg, rgba(33,150,243,0.12) 0%, rgba(25,118,210,0.08) 100%)',
                borderRadius: '25px',
                border: '2px solid rgba(33,150,243,0.25)',
                display: 'flex',
                justifyContent: 'center',
                gap: '50px',
                flexWrap: 'wrap',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Live indicator */}
                <div style={{
                  position: 'absolute',
                  top: '10px',
                  right: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#4caf50',
                    animation: 'pulse 2s ease-in-out infinite'
                  }} />
                  <Typography style={{ fontSize: '0.7rem', color: '#4caf50', fontWeight: 600 }}>
                    LIVE
              </Typography>
                </div>

                <div style={{ textAlign: 'center', animation: 'countUp 2s ease-out' }}>
                  <Typography variant="h4" style={{ 
                    fontWeight: 700, 
                    color: '#1976d2', 
                    marginBottom: '5px',
                    fontFamily: 'monospace'
                  }}>
                    {loading ? '...' : (!metricsError ? `${formatFullVolume(realMetrics.totalWaterReplenished)}m³` :
                      <>
                        <ErrorOutline style={{ fontSize: '20px', color: '#ef4444' }} />
                        <span style={{ fontSize: '0.8rem', color: '#ef4444' }}>Error</span>
                      </>
                    )}
                  </Typography>
                  <Typography style={{ color: '#666', fontSize: '0.9rem', fontWeight: 500 }}>
                    Water Successfully Saved (All Time)
                  </Typography>
                </div>
                <div style={{ textAlign: 'center', animation: 'countUp 2s ease-out 0.5s both' }}>
                  <Typography variant="h4" style={{ 
                    fontWeight: 700, 
                    color: '#1976d2', 
                    marginBottom: '5px',
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    {loading ? '...' : (!metricsError ? realMetrics.activeStupas :
                      <>
                        <ErrorOutline style={{ fontSize: '20px', color: '#ef4444' }} />
                        <span style={{ fontSize: '0.8rem', color: '#ef4444' }}>Error</span>
                      </>
                    )}
                  </Typography>
                  <Typography style={{ color: '#666', fontSize: '0.9rem', fontWeight: 500 }}>
                    Active Stupas (Current)
                  </Typography>
                </div>
                <div style={{ textAlign: 'center', animation: 'countUp 2s ease-out 1s both' }}>
                  <Typography variant="h4" style={{ 
                    fontWeight: 700, 
                    color: '#1976d2', 
                    marginBottom: '5px',
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    {loading ? '...' : yearsSinceFirstStupa}
                  </Typography>
                  <Typography style={{ color: '#666', fontSize: '0.9rem', fontWeight: 500 }}>
                    Years Since First Stupa
                  </Typography>
                </div>
              </div>
            </div>

            {/* CSS Animations */}
            <style>
              {`
                @keyframes heroFloat {
                  0%, 100% { transform: translateY(0px); }
                  50% { transform: translateY(-10px); }
                }
                @keyframes waterDrop {
                  0%, 100% { transform: translateY(0px) scale(1); opacity: 0.6; }
                  50% { transform: translateY(-20px) scale(1.2); opacity: 1; }
                }
                @keyframes crystalFloat {
                  0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0.4; }
                  50% { transform: translateY(-15px) rotate(180deg); opacity: 0.8; }
                }
                @keyframes titlePulse {
                  0%, 100% { filter: drop-shadow(0 0 20px rgba(33,150,243,0.3)); }
                  50% { filter: drop-shadow(0 0 40px rgba(33,150,243,0.6)); }
                }
                @keyframes underlineGlow {
                  0%, 100% { box-shadow: 0 0 10px rgba(25,118,210,0.3); }
                  50% { box-shadow: 0 0 25px rgba(25,118,210,0.8); }
                }
                @keyframes imageFloat1 {
                  0%, 100% { transform: translateY(0px) rotate(0deg); }
                  50% { transform: translateY(-15px) rotate(5deg); }
                }
                @keyframes imageFloat2 {
                  0%, 100% { transform: scale(1.1) translateY(0px) rotate(0deg); }
                  50% { transform: scale(1.15) translateY(-20px) rotate(-5deg); }
                }
                @keyframes imageFloat3 {
                  0%, 100% { transform: translateY(0px) rotate(0deg); }
                  50% { transform: translateY(-10px) rotate(3deg); }
                }
                @keyframes textGlow {
                  0%, 100% { filter: drop-shadow(0 0 5px rgba(25,118,210,0.3)); }
                  50% { filter: drop-shadow(0 0 15px rgba(25,118,210,0.6)); }
                }
                @keyframes shimmer {
                  0% { left: -100%; }
                  100% { left: 100%; }
                }
                @keyframes countUp {
                  0% { transform: translateY(20px); opacity: 0; }
                  100% { transform: translateY(0px); opacity: 1; }
                }
                @keyframes pulse {
                  0% { opacity: 0.5; }
                  50% { opacity: 1; }
                  100% { opacity: 0.5; }
                }
                @keyframes floatHeart {
                  0% { 
                    transform: translateY(0px) scale(0.8) rotate(0deg); 
                    opacity: 0; 
                  }
                  10% { 
                    opacity: 1; 
                    transform: translateY(-20px) scale(1) rotate(-5deg); 
                  }
                  50% { 
                    transform: translateY(-150px) scale(1.1) rotate(10deg); 
                    opacity: 0.8; 
                  }
                  100% { 
                    transform: translateY(-300px) scale(0.6) rotate(-10deg); 
                    opacity: 0; 
                  }
                }
                @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% {
                    transform: translateY(0);
                  }
                  40% {
                    transform: translateY(-10px);
                  }
                  60% {
                    transform: translateY(-5px);
                  }
                }
              `}
            </style>
          </div>

          {/* Real Impact Stories Carousel - Floating over video */}
          <div style={{ 
            marginBottom: '60px',
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '30px',
            padding: '60px 40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            border: '1px solid rgba(33,150,243,0.2)',
            backdropFilter: 'blur(10px)'
          }}>
          <Typography
            variant="h2"
              style={{ 
              textAlign: 'center',
                fontSize: '3rem',
                fontWeight: 300,
                marginBottom: '60px',
                color: '#1565c0'
            }}
          >
              Stories from the Field
          </Typography>

            {/* Horizontal Scrolling Carousel */}
            <div style={{ 
              position: 'relative',
              maxWidth: '1200px',
              margin: '0 auto',
              overflow: 'hidden',
              padding: '0 80px' // Space for navigation arrows
            }}>
              {/* Stories Container */}
              <div 
                style={{
                  display: 'flex',
                  gap: '30px',
                  transform: `translateX(-${currentStoryIndex * 320}px)`,
                  transition: isTransitioning ? 'transform 0.5s ease-in-out' : 'none',
                  width: `${extendedStories.length * 320}px`
                }}
              >
                {extendedStories.map((story, index) => {
                  // Calculate the actual story index for styling
                  const actualIndex = index === 0 ? stories.length - 1 : 
                                    index === extendedStories.length - 1 ? 0 : 
                                    index - 1;
                  const isActive = index === currentStoryIndex;
                  
                  return (
                    <Card 
                      key={`${index}-${story.title}`}
                      onClick={() => {
                        if (story.link) {
                          window.open(story.link, '_blank', 'noopener,noreferrer');
                        }
                      }}
                      style={{
                        minWidth: '290px',
                        borderRadius: '20px',
                        overflow: 'hidden',
                        border: '2px solid rgba(33,150,243,0.1)',
                        boxShadow: isActive 
                          ? '0 25px 60px rgba(0,0,0,0.2)' 
                          : '0 15px 40px rgba(0,0,0,0.1)',
                        transform: isActive ? 'scale(1.05)' : 'scale(1)',
                        transition: 'all 0.5s ease-in-out',
                        opacity: isActive ? 1 : 0.8,
                        cursor: story.link ? 'pointer' : 'default'
                      }}
                      onMouseEnter={(e: React.MouseEvent<HTMLDivElement>) => {
                        if (story.link) {
                          e.currentTarget.style.transform = isActive ? 'scale(1.08)' : 'scale(1.03)';
                          e.currentTarget.style.borderColor = 'rgba(33,150,243,0.3)';
                        }
                      }}
                      onMouseLeave={(e: React.MouseEvent<HTMLDivElement>) => {
                        e.currentTarget.style.transform = isActive ? 'scale(1.05)' : 'scale(1)';
                        e.currentTarget.style.borderColor = 'rgba(33,150,243,0.1)';
                      }}
                    >
                      <div style={{
                        height: '200px',
                        backgroundImage: `url(${story.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}>
                        {/* Overlay */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(135deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.6) 100%)'
                        }} />
                        
                        {/* Location Badge */}
                        <div style={{
                          position: 'absolute',
                          top: '15px',
                          left: '15px',
                          background: 'rgba(0,0,0,0.7)',
                          color: 'white',
                          padding: '5px 12px',
                          borderRadius: '15px',
                          fontSize: '0.8rem',
                          fontWeight: 500,
                          backdropFilter: 'blur(10px)'
                        }}>
                          {story.type === 'linkedin' ? '🔗' : '📰'} {story.location}
                        </div>
                        

                      </div>
                      
                      {/* Card Content */}
                      <CardContent style={{ padding: '20px' }}>
                        <Typography variant="h6" style={{ 
                    fontWeight: 600,
                          marginBottom: '10px',
                          color: '#1565c0',
                          fontSize: '1.1rem'
                        }}>
                          {story.title}
                </Typography>
                        <Typography style={{ 
                          color: '#666',
                          fontSize: '0.95rem',
                          lineHeight: 1.5
                        }}>
                          {story.description}
                </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Navigation Arrows */}
              <Button
                onClick={prevStory}
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  minWidth: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  border: '2px solid rgba(33,150,243,0.2)',
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  zIndex: 10,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1.1)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 12px 35px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
              >
                ‹
                </Button>
              
              <Button
                onClick={nextStory}
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  minWidth: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                  border: '2px solid rgba(33,150,243,0.2)',
                  color: '#1976d2',
                  fontSize: '1.5rem',
                  zIndex: 10,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1.1)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 12px 35px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
                  (e.target as HTMLButtonElement).style.transform = 'translateY(-50%) scale(1)';
                  (e.target as HTMLButtonElement).style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                }}
              >
                ›
              </Button>

              {/* Progress Indicator */}
              <div style={{
                position: 'absolute',
                bottom: '-40px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                {stories.map((_, index) => {
                  // Calculate if this dot should be active
                  const isActiveDot = (currentStoryIndex === index + 1) || 
                                     (currentStoryIndex === 0 && index === stories.length - 1) ||
                                     (currentStoryIndex === stories.length + 1 && index === 0);
                  
                  return (
                    <div
                      key={index}
                      style={{
                        width: isActiveDot ? '24px' : '8px',
                        height: '8px',
                        borderRadius: '4px',
                        background: isActiveDot 
                          ? '#1976d2' 
                          : 'rgba(25,118,210,0.3)',
                        transition: 'all 0.3s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        if (!isTransitioning) {
                          setIsTransitioning(true);
                          setCurrentStoryIndex(index + 1); // +1 because of the prepended item
                  }
                }}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {/* Unified Challenge & Solution with Live Metrics */}
          <div style={{ 
            marginBottom: '60px',
            background: 'rgba(255,255,255,0.95)',
            borderRadius: '30px',
            padding: '80px 60px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
            border: '1px solid rgba(33,150,243,0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <Typography 
              variant="h2" 
              style={{ 
                textAlign: 'center',
                fontSize: '3rem',
                fontWeight: 300,
                marginBottom: '60px',
                color: '#1565c0'
              }}
            >
              The Challenge We're Solving
              </Typography>
              
            <Grid container spacing={6} alignItems="stretch">
              <Grid item xs={12} md={6}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(33,150,243,0.08) 0%, rgba(25,118,210,0.12) 100%)',
                  padding: '50px 40px',
                  borderRadius: '24px',
                  border: '2px solid rgba(33,150,243,0.2)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 8px 32px rgba(33,150,243,0.1)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Background Pattern */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 20% 80%, rgba(33,150,243,0.03) 0%, transparent 50%)',
                    pointerEvents: 'none'
                  }} />
                  
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(25,118,210,0.3)'
                      }}>
                        <span style={{ fontSize: '24px' }}>🌊</span>
                      </div>
                      <Typography variant="h3" style={{ 
                        color: '#1565c0', 
                        fontWeight: 600,
                        fontSize: '2rem',
                        margin: 0
                      }}>
                        The Crisis
                  </Typography>
                    </div>
                    
                    <Typography style={{ 
                      fontSize: '1.25rem', 
                      lineHeight: 1.7, 
                      color: '#37474f',
                      marginBottom: '20px',
                      fontWeight: 400
                    }}>
                      Across the world, glaciers are retreating -- and some regions hold far more than others.
                  </Typography>
                  </div>
                
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.8)',
                      padding: '24px',
                      borderRadius: '16px',
                      border: '1px solid rgba(33,150,243,0.15)',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(33,150,243,0.08)',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: '#1976d2',
                        letterSpacing: '-1px',
                        lineHeight: 1.1,
                        marginBottom: '6px',
                        textShadow: '0 2px 12px rgba(25,118,210,0.10)'
                      }}>
                        10%
                      </span>
                      <Typography style={{
                        fontWeight: 600,
                        color: '#1565c0',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        of Global Glaciers are Located in Chile
                  </Typography>
                    </div>
                    
                    <div style={{
                      background: 'rgba(255,255,255,0.8)',
                      padding: '24px',
                      borderRadius: '16px',
                      border: '1px solid rgba(33,150,243,0.15)',
                      boxShadow: '0 4px 12px rgba(33,150,243,0.08)',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: '#1976d2',
                        letterSpacing: '-1px',
                        lineHeight: 1.1,
                        marginBottom: '6px',
                        textShadow: '0 2px 12px rgba(25,118,210,0.10)'
                      }}>
                        35%
                      </span>
                      <Typography style={{
                        fontWeight: 600,
                        color: '#1565c0',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        Higher Melt Rate than Global Average
                  </Typography>
                    </div>
                  </div>
                </div>
                </Grid>
                
              <Grid item xs={12} md={6}>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(33,150,243,0.12) 0%, rgba(25,118,210,0.16) 100%)',
                  padding: '50px 40px',
                  borderRadius: '24px',
                  border: '2px solid rgba(33,150,243,0.25)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  boxShadow: '0 8px 32px rgba(33,150,243,0.15)',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Background Pattern */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at 80% 20%, rgba(33,150,243,0.04) 0%, transparent 50%)',
                    pointerEvents: 'none'
                  }} />
                  
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '28px'
                    }}>
                      <div style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(25,118,210,0.3)'
                      }}>
                        <span style={{ fontSize: '24px' }}>❄️</span>
                      </div>
                      <Typography variant="h3" style={{ 
                        color: '#1565c0', 
                        fontWeight: 600,
                        fontSize: '2rem',
                        margin: 0
                      }}>
                        Our Solution
                  </Typography>
                    </div>
                    
                    <Typography style={{ 
                      fontSize: '1.25rem', 
                      lineHeight: 1.7, 
                      color: '#37474f',
                      marginBottom: '20px',
                      fontWeight: 400
                    }}>
                      Nilus restores mountain ecosystems through artificial ice reservoirs rooted in Himalayan knowledge.
                  </Typography>
                  </div>
                  
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{
                      background: 'rgba(255,255,255,0.8)',
                      padding: '24px',
                      borderRadius: '16px',
                      border: '1px solid rgba(33,150,243,0.2)',
                      marginBottom: '16px',
                      boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: '#1976d2',
                        letterSpacing: '-1px',
                        lineHeight: 1.1,
                        marginBottom: '6px',
                        textShadow: '0 2px 12px rgba(25,118,210,0.10)'
                      }}>
                        20,000+ m³
                      </span>
                      <Typography style={{
                        fontWeight: 600,
                        color: '#1565c0',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        Water Replenished per Icestupa
                      </Typography>
                    </div>
                    
                    <div style={{
                      background: 'rgba(255,255,255,0.8)',
                      padding: '24px',
                      borderRadius: '16px',
                      border: '1px solid rgba(33,150,243,0.2)',
                      boxShadow: '0 4px 12px rgba(33,150,243,0.1)',
                      backdropFilter: 'blur(8px)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}>
                      <span style={{
                        fontSize: '2.4rem',
                        fontWeight: 800,
                        color: '#1976d2',
                        letterSpacing: '-1px',
                        lineHeight: 1.1,
                        marginBottom: '6px',
                        textShadow: '0 2px 12px rgba(25,118,210,0.10)'
                      }}>
                        {loading ? 'Loading...' : `${formatFullVolume(realMetrics.totalWaterReplenished)} m³`}
                      </span>
                      <Typography style={{
                        fontWeight: 600,
                        color: '#1565c0',
                        fontSize: '1.1rem',
                        textAlign: 'center',
                        lineHeight: 1.5,
                        margin: 0
                      }}>
                        Water Successfully Allocated for Ecosystem
                      </Typography>
                    </div>
                  </div>
                </div>
                </Grid>
              </Grid>
          </div>

          {/* Call to Action - Support Our Mission */}
          <div ref={supportSectionRef} style={{ 
            marginBottom: '60px',
              textAlign: 'center',
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            borderRadius: '30px',
            padding: '80px 60px',
            color: 'white',
            boxShadow: '0 20px 60px rgba(25,118,210,0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Floating Hearts Container */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              pointerEvents: 'none',
              zIndex: 10
            }}>
              {floatingHearts.map((heart) => (
                <div
                  key={heart.id}
                  style={{
                    position: 'absolute',
                    left: `calc(50% + ${heart.x}px)`,
                    bottom: '200px',
                    animation: `floatHeart 3s ease-out forwards`,
                    zIndex: heart.type === 'user' ? 12 : 11
                  }}
                >
                  {heart.type === 'user' ? (
                    // User heart: White heart in blue circle (prominent)
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 20px rgba(25,118,210,0.8)',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>
                      <Favorite style={{ 
                        fontSize: '24px', 
                        color: 'white',
                        filter: 'drop-shadow(0 0 5px rgba(255,255,255,0.5))'
                      }} />
                    </div>
                  ) : (
                    // Global heart: Semi-transparent blue heart (subtle)
                    <div style={{
                      width: '35px',
                      height: '35px',
                      borderRadius: '50%',
                      background: 'rgba(25,118,210,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 10px rgba(25,118,210,0.4)',
                      border: '1px solid rgba(255,255,255,0.2)'
                    }}>
                      <Favorite style={{ 
                        fontSize: '18px', 
                        color: 'rgba(255,255,255,0.8)'
                      }} />
                    </div>
                  )}
                </div>
              ))}
            </div>

          <Typography
            variant="h2"
              style={{ 
                fontSize: '3rem',
                fontWeight: 300,
                marginBottom: '30px'
              }}
            >
              Help Us Build the Next Stupa
          </Typography>

                <Typography 
              style={{ 
                fontSize: '1.4rem',
                marginBottom: '50px',
                opacity: 0.95,
                maxWidth: '700px',
                margin: '0 auto 50px auto',
                lineHeight: 1.6
                  }}
                >
              We believe that this technology helps create hope, security and a sustainable future for people around the world. Do you like this vision?
                </Typography>
            
            {/* Action Buttons Row */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '30px', 
              flexWrap: 'wrap',
              marginBottom: '60px'
            }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  window.location.href = 'mailto:info@nilus.eco?subject=Connect With Us - Nilus Project';
                }}
                style={{ 
                  padding: '20px 50px',
                  fontSize: '1.3rem',
                    fontWeight: 600,
                  background: 'white',
                  color: '#1976d2',
                  borderRadius: '50px',
                  textTransform: 'none',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                }}
              >
                Connect With Us
              </Button>

              <Button
                variant="contained"
                size="large"
                startIcon={<Favorite />}
                onClick={async () => {
                  setNiceCount((c) => c + 1);
                  createUserHeart();
                  
                  // Increment global count via API
                  try {
                    const response = await publicFetch(`${API_URL}/likes/increment`, {
                      method: 'POST'
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setGlobalNiceCount(data.globalLikeCount);
                    }
                  } catch (error) {
                    console.error('Error incrementing likes:', error);
                    // Fallback to local increment if API fails
                    setGlobalNiceCount((c) => c + 1);
                  }
                }}
                style={{ 
                  padding: '20px 50px', 
                  fontSize: '1.3rem', 
                    fontWeight: 600,
                  background: niceCount > 0 
                    ? 'linear-gradient(135deg, #4caf50 0%, #81c784 100%)'
                    : 'rgba(255,255,255,0.2)',
                  borderRadius: '50px',
                  border: '2px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  cursor: 'pointer',
                  textTransform: 'none',
                  transition: 'all 0.3s ease'
                  }}
                >
                NIIIIIIICE {niceCount > 0 && `(+${niceCount})`}
              </Button>
            </div>

            {/* Global Counter Display */}
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: '20px',
              padding: '25px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)',
              textAlign: 'center'
            }}>
              <Typography variant="h5" style={{ 
                marginBottom: '10px',
                fontWeight: 300,
                opacity: 0.9
              }}>
                Global Support Counter
                </Typography>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '10px',
                marginBottom: '10px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#4caf50',
                  animation: 'pulse 2s ease-in-out infinite'
                }} />
                <Typography variant="h3" style={{ 
                  fontWeight: 700,
                  fontFamily: 'monospace',
                  color: 'white'
                }}>
                  {globalNiceCount}
                </Typography>
              </div>
              
              <Typography style={{ 
                opacity: 0.8,
                fontSize: '1rem'
              }}>
                People worldwide have shown their support
              </Typography>
              
              {niceCount > 0 && (
                <Typography style={{ 
                  marginTop: '15px', 
                  opacity: 0.9,
                  fontSize: '1.1rem',
                  fontWeight: 500
                }}>
                  {niceCount === 1 && "🌊 Thank you for your support!"}
                  {niceCount > 1 && niceCount < 10 && "❄️ You're helping spread awareness!"}
                  {niceCount >= 10 && niceCount < 50 && "🏔️ You're a true water champion!"}
                  {niceCount >= 50 && "🌍 You're changing the world, one click at a time!"}
                </Typography>
              )}
            </div>
          </div>
        </Container>
      </div>

      {/* Interactive Map Section */}
      <div style={{
        position: 'relative',
        height: '100vh',
        zIndex: 3,
        background: 'transparent'
      }}>
        {/* Single Interactive Map */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 10
        }}>
          <TerrainMap />
        </div>

        {/* Semi-transparent Overlay with Navigation Text - Top portion only */}
        <div 
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '200px',
            background: 'linear-gradient(180deg, rgba(25,118,210,0.3) 0%, rgba(33,150,243,0.2) 70%, rgba(42,165,245,0.05) 100%)',
            zIndex: 11,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto'
          }}
              >
                <Typography 
            variant="h3"
            style={{
              color: 'white',
              fontSize: '2.5rem',
                    fontWeight: 600,
              marginBottom: '10px',
              textAlign: 'center',
              textShadow: '0 2px 10px rgba(0,0,0,0.7)',
              letterSpacing: '0.5px'
                  }}
                >
            Explore Stupa Locations
                </Typography>

          <Typography 
            style={{
              color: 'rgba(255,255,255,0.95)',
              fontSize: '1.1rem',
              fontWeight: 400,
              textAlign: 'center',
              textShadow: '0 1px 8px rgba(0,0,0,0.6)',
              letterSpacing: '0.3px'
            }}
          >
            Click and drag to explore • Scroll up to return to main content
                </Typography>
        </div>

        {/* Simplified Map Legend */}
        <div style={{
          position: 'absolute',
          bottom: '30px',
          left: '30px',
          background: 'rgba(255,255,255,0.95)',
          padding: '15px 20px',
          borderRadius: '12px',
          fontSize: '0.85rem',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          zIndex: 12,
          border: '1px solid rgba(33,150,243,0.2)',
          backdropFilter: 'blur(10px)',
          maxWidth: '250px'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src="/images/existing_stupa.png" 
                alt="Active Stupa"
                style={{
                  width: '24px',
                  height: '24px',
                  objectFit: 'contain'
                }}
              />
              <span style={{ color: '#424242', fontWeight: 500, fontSize: '0.85rem' }}>
                Active stupas
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <img 
                src="/images/donate_stupa.png" 
                alt="Planned Stupa"
                style={{
                  width: '24px',
                  height: '24px',
                  objectFit: 'contain'
                }}
              />
              <span style={{ color: '#424242', fontWeight: 500, fontSize: '0.85rem' }}>
                Planned locations
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;